import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useHistory } from 'react-router';
import { v4 } from 'uuid';
import { QuestionType } from '../../../../__legacy__/modules/annotation/question.model';
import { IProjectDetailsBl } from '../../../../__legacy__/modules/projectDetails/projectDetails.bl';
import { IProjectDetailsToolsBl } from '../../../../__legacy__/modules/projectDetails/sub/tools/projectDetailsTools.bl';
import { IQuestion } from '../../../../__legacy__/modules/projectDetails/sub/tools/projectDetailsTools.models';
import { Home } from '../../../../__legacy__/routes/config/Home';
import { TRAINING_VARIANT } from '../../../jobs/jobs.model';
import { ICurrentWorkspaceStore } from '../../../workspaces/currentWorkspace/CurrentWorkspace.store';
import { AddMarkingToolBl } from '../../details/annotationTools/markingTools/add/AddMarkingTool.bl';
import { MARKING_TOOL_NAME, QUESTION_ALTERNATIVE_ANSWERS, QUESTION_ALTERNATIVE_TEXT, QUESTION_STATUS_ANSWERS, QUESTION_STATUS_TEXT } from './constants';
import { CreateProjectModal } from './ui/CreateProjectModal';

export const CreateProjectContainer = observer((): ReactElement => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const projectsDetailsBl = useInjection(IProjectDetailsBl.$);
  const projectDetailsToolsBl = useInjection(IProjectDetailsToolsBl.$);
  const addMarkingToolBl = useInjection(AddMarkingToolBl.$);
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [isShown, setIsShown] = useState(false);

  const handleCreateAnnotationTemplate = async (id: string, trainingVariant: TRAINING_VARIANT | null): Promise<void> => {
    if (!trainingVariant) {
      return;
    }

    const result = await projectsDetailsBl.getProjectAnnotationTemplate(trainingVariant);

    if (result.question) {
      let params: IQuestion = {
        id: v4(),
        type: result.question,
        isRequired: true,
        scopes: ['Image'],
        isUsed: false,
        order: 0,
        questionText: '',
        answers: [],
      };

      switch (result.question) {
        case QuestionType.STATUS_GOOD_BAD:
          params = {
            ...params,
            answers: QUESTION_STATUS_ANSWERS,
            questionText: QUESTION_STATUS_TEXT,
          };
          break;
        case QuestionType.ALTERNATIVE:
          params = {
            ...params,
            answers: QUESTION_ALTERNATIVE_ANSWERS,
            questionText: QUESTION_ALTERNATIVE_TEXT,
          };
          break;
        case QuestionType.TRUE_FALSE:
        case QuestionType.MULTISELECT:
        case QuestionType.OPEN:
        case QuestionType.NUMBER:
        case QuestionType.SET_TRAIN_TEST:
          break;
      }

      await projectDetailsToolsBl.addQuestionAsync(params, id);
    }

    if (result.marking_tool) {
      await addMarkingToolBl.createTemplateMarkingTool(id, MARKING_TOOL_NAME, result.marking_tool);
    }
  };

  const handleCreateProjectClicked = async (trainingVariant: TRAINING_VARIANT | null): Promise<void> => {
    setIsLoading(true);

    const id = v4();
    const projectName = `Project ${new Date().toLocaleString()}`;
    await projectsDetailsBl.createProjectAsync(id, projectName, trainingVariant);
    await handleCreateAnnotationTemplate(id, trainingVariant);

    history.push(Home.Projects.Details.Datasets.withParams({ projectId: id, workspaceId: currentWorkspaceStore.currentWorkspace.id }));

    setIsLoading(false);
    toggleModal();
  };

  const toggleModal = (): void => {
    if (isLoading) {
      return;
    }

    setIsShown((state) => !state);
  };

  return (
    <CreateProjectModal
      isShown={isShown}
      handleCreateProjectClicked={handleCreateProjectClicked}
      toggleModal={toggleModal}
      isLoading={isLoading}
    />
  );
});
